"use client";

import Link from "next/link";
import {
	BookOpen,
	ChevronDown,
	ChevronUp,
	FolderOpen,
	Home,
	Mail,
	ShieldCheck,
	UserRound,
} from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";

type NavItem = {
	href: string;
	label: string;
	description: string;
	icon: LucideIcon;
};

const navItems: NavItem[] = [
	{
		href: "/",
		label: "Home",
		description: "Start here and get a quick overview.",
		icon: Home,
	},
	{
		href: "/blog",
		label: "Blog",
		description: "Notes, tutorials, and front-end writeups.",
		icon: BookOpen,
	},
	{
		href: "/projects",
		label: "Projects",
		description: "A closer look at selected work.",
		icon: FolderOpen,
	},
	{
		href: "/certifications",
		label: "Certifications",
		description: "Learning tracks and verified credentials.",
		icon: ShieldCheck,
	},
	{
		href: "/about",
		label: "About",
		description: "More about my background and experience.",
		icon: UserRound,
	},
	{
		href: "/contact",
		label: "Contact",
		description: "Reach out for collaborations or discussions.",
		icon: Mail,
	},
];

export default function SiteHeader({
	sticky = false,
}: {
	sticky?: boolean;
}) {
	const pathname = usePathname();
	const [isVisible, setIsVisible] = useState(true);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const lastScrollYRef = useRef(0);
	const tickingRef = useRef(false);

	useEffect(() => {
		if (!sticky) return;

		lastScrollYRef.current = window.scrollY;

		const onScroll = () => {
			if (tickingRef.current) return;
			tickingRef.current = true;

			window.requestAnimationFrame(() => {
				const currentY = window.scrollY;
				const deltaY = currentY - lastScrollYRef.current;
				const absDelta = Math.abs(deltaY);

				// Keep nav visible near the top.
				if (currentY < 24) {
					setIsVisible(true);
				} else if (absDelta > 14) {
					if (deltaY > 0 && currentY > 110) {
						setIsVisible(false);
					} else if (deltaY < 0) {
						setIsVisible(true);
					}
				}

				lastScrollYRef.current = currentY;
				tickingRef.current = false;
			});
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, [sticky]);

	useEffect(() => {
		setIsMenuOpen(false);
	}, [pathname]);

	return (
		<header
			className={
				sticky
					? `fixed inset-x-0 top-7 z-30 pointer-events-none transition-all duration-300 ease-out md:top-8 ${
							isVisible ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0"
						}`
					: "relative z-30"
			}
		>
			<nav className="mx-auto flex w-full max-w-6xl items-center justify-center px-4 sm:px-6">
				<div className="pointer-events-auto hidden items-center rounded-2xl border border-white/8 bg-[#11151c] px-4 py-2.5 shadow-[0_14px_30px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.03)] md:flex">
					{navItems.map((item) => {
						const isActive = pathname === item.href;

						return (
							<Link
								key={item.href}
								href={item.href}
								className={`px-4 text-[1.03rem] font-medium transition ${
									isActive ? "text-white" : "text-zinc-300/95 hover:text-zinc-100"
								}`}
							>
								{item.label}
							</Link>
						);
					})}
				</div>

				<div className="pointer-events-auto relative flex w-full justify-end md:hidden">
					<div className="w-full max-w-[18.5rem]">
						<div className="flex justify-end">
							<button
								type="button"
								onClick={() => setIsMenuOpen((prev) => !prev)}
								aria-expanded={isMenuOpen}
								aria-label="Toggle navigation menu"
								className="inline-flex items-center gap-2 rounded-[0.65rem] border border-white/8 bg-white/[0.05] px-3.5 py-2 text-[0.95rem] font-medium text-zinc-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition hover:border-white/12 hover:bg-white/[0.08]"
							>
								<span>Menu</span>
								{isMenuOpen ? (
									<ChevronUp className="h-4 w-4 text-zinc-300" />
								) : (
									<ChevronDown className="h-4 w-4 text-zinc-300" />
								)}
							</button>
						</div>

						{isMenuOpen ? (
							<div className="mt-2.5 rounded-[0.8rem] border border-white/8 bg-[#101012]/92 p-2 shadow-[0_18px_40px_rgba(0,0,0,0.28)] backdrop-blur-xl">
								<div className="space-y-2">
									{navItems.map((item) => {
										const isActive = pathname === item.href;
										const Icon = item.icon;

										return (
											<Link
												key={item.href}
												href={item.href}
												className={`flex items-center gap-2 rounded-[0.65rem] border px-2 py-1.5 transition ${
													isActive
														? "border-white/10 bg-white/[0.06]"
														: "border-white/[0.03] bg-white/[0.025] hover:border-white/8 hover:bg-white/[0.045]"
												}`}
											>
												<span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.45rem] border border-white/6 bg-white/[0.03] text-zinc-300">
													<Icon className="h-[0.9rem] w-[0.9rem]" strokeWidth={1.8} />
												</span>
												<span className="min-w-0 flex-1">
													<span className="block text-[0.88rem] font-medium text-zinc-100">
														{item.label}
													</span>
													<span className="mt-0.5 block text-[0.74rem] leading-snug text-zinc-400">
														{item.description}
													</span>
												</span>
												{isActive ? (
													<span className="shrink-0 rounded-full border border-white/8 bg-white/[0.04] px-1.5 py-0.5 text-[0.62rem] font-medium text-zinc-400">
														Current
													</span>
												) : null}
											</Link>
										);
									})}
								</div>
							</div>
						) : null}
					</div>
				</div>
			</nav>
		</header>
	);
}
